import { useCallback, useRef, type ReactNode } from 'react'

interface UseFolderPickerOptions {
  /** Every file in the chosen folder and its subfolders, unfiltered. */
  onFiles: (files: File[]) => void
  disabled?: boolean
}

interface UseFolderPickerResult {
  openFolderPicker: () => void
  /** Render once, anywhere — the hidden `<input>` the picker needs. */
  folderInput: ReactNode
}

/**
 * "Upload folder" for upload surfaces: a hidden directory input. The browser
 * hands back every file under the folder — `.DS_Store` and all — so callers
 * pass the files through `expandUploadFiles`, which drops the system files.
 *
 * `webkitdirectory` is set on the DOM node rather than as a JSX prop: React's
 * types don't know it, and every current browser supports the prefixed name.
 */
export function useFolderPicker({
  onFiles,
  disabled = false,
}: UseFolderPickerOptions): UseFolderPickerResult {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const setInputRef = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node
    if (node) node.setAttribute('webkitdirectory', '')
  }, [])

  const openFolderPicker = useCallback(() => {
    if (disabled) return
    inputRef.current?.click()
  }, [disabled])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    // Reset so choosing the same folder again still fires a change.
    e.target.value = ''
    if (files.length > 0) onFiles(files)
  }

  const folderInput = (
    <input
      ref={setInputRef}
      type="file"
      multiple
      className="hidden"
      tabIndex={-1}
      aria-hidden="true"
      disabled={disabled}
      onChange={handleChange}
      data-testid="folder-picker-input"
    />
  )

  return { openFolderPicker, folderInput }
}
